import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Icon, type IconName } from './icon.tsx'

const cardImageTextExtVariants = cva(
	'relative overflow-hidden bg-white transition-all duration-300 ease-in-out',
	{
		variants: {
			state: {
				default: 'border-[2.5px] border-neutral-400',
				selected: 'border-[2.5px] border-blue-500',
			},
		},
		defaultVariants: {
			state: 'default',
		},
	},
)

export interface CardImageTextExtProps
	extends Omit<React.ComponentPropsWithoutRef<'div'>, 'style'>,
		VariantProps<typeof cardImageTextExtVariants> {
	/**
	 * If set to `true`, the component will be rendered as a child within the component.
	 * This child component must be a valid React component.
	 */
	asChild?: boolean
	/**
	 * Card visual layout - vertical or horizontal
	 */
	layout?: 'vertical' | 'horizontal'
	/**
	 * Card variant style - standard (default with side image) or coverage (large centered image)
	 */
	variant?: 'standard' | 'coverage'
	/**
	 * Custom inline styles (native HTML style attribute)
	 */
	style?: React.CSSProperties
	/**
	 * Show/hide image
	 */
	showImage?: boolean
	/**
	 * Image source URL
	 */
	image?: string
	/**
	 * Image alt text for accessibility
	 */
	imageAlt?: string
	/**
	 * Show/hide chip/tag
	 */
	showChip?: boolean
	/**
	 * Chip/tag text content
	 */
	chipText?: string
	/**
	 * Show/hide icon next to title
	 */
	showIcon?: boolean
	/**
	 * Icon name to display
	 */
	icon?: IconName
	/**
	 * Custom icon element (overrides icon name)
	 */
	iconElement?: React.ReactNode
	/**
	 * Card title text
	 */
	title?: string
	/**
	 * Show/hide description
	 */
	showDescription?: boolean
	/**
	 * Card description text
	 */
	description?: string
	/**
	 * Show/hide primary button
	 */
	showButton?: boolean
	/**
	 * Primary button text
	 */
	buttonText?: string
	/**
	 * Primary button click handler
	 */
	onButtonClick?: () => void
	/**
	 * Show/hide secondary button
	 */
	showButton2?: boolean
	/**
	 * Secondary button text
	 */
	button2Text?: string
	/**
	 * Secondary button click handler
	 */
	onButton2Click?: () => void
	/**
	 * Make the entire card clickable (for coverage variant)
	 */
	clickable?: boolean
	/**
	 * Card click handler (when clickable is true)
	 */
	onCardClick?: () => void
}

const CardImageTextExt = React.forwardRef<
	HTMLDivElement,
	CardImageTextExtProps
>(
	(
		{
			className,
			layout = 'horizontal',
			variant = 'standard',
			state = 'default',
			asChild = false,
			showImage = true,
			image = '/img/proposito/proposito1.png',
			imageAlt = 'Card image',
			showChip = false,
			chipText = 'Terceros full',
			showIcon = false,
			icon,
			iconElement,
			title = 'Título card',
			showDescription = true,
			description = 'Escribe una descripción increíble en esta sección de tarjeta dedicada.',
			showButton = true,
			buttonText = 'Botón',
			onButtonClick,
			showButton2 = false,
			button2Text = 'Botón',
			onButton2Click,
			clickable = false,
			onCardClick,
			...props
		},
		ref,
	) => {
		const Comp = asChild ? Slot : clickable ? 'button' : 'div'

		return (
			<Comp
				ref={ref}
				className={cn(
					cardImageTextExtVariants({ state }),
					clickable && 'cursor-pointer transition-all',
					className,
				)}
				onClick={clickable ? onCardClick : undefined}
				{...props}
			>
				{/* Coverage variant - vertical layout with large centered image */}
				{variant === 'coverage' ? (
					<div className="flex h-full flex-col items-center gap-4 p-6">
						{/* Image container - flex-1 to fill available space */}
						{showImage && (
							<div className="flex flex-1 items-center justify-center overflow-hidden rounded-xl">
								<img
									src={image}
									alt={imageAlt}
									className="h-48 w-48 object-contain"
								/>
							</div>
						)}

						{/* Content */}
						<div className="flex w-full flex-col items-start gap-3">
							{/* Chip/Tag */}
							{showChip && (
								<div className="rounded-sm bg-green-500 px-2 py-0.5">
									<p className="font-parkinsans text-sm leading-5 font-medium text-white">
										{chipText}
									</p>
								</div>
							)}

							{/* Text content */}
							<div className="flex w-full flex-col gap-3">
								<h3 className="font-parkinsans text-[24px] leading-8 font-medium text-blue-800">
									{title}
								</h3>
								{showDescription && (
									<p className="font-inter text-base leading-[26px] font-normal text-[#454545]">
										{description}
									</p>
								)}
							</div>
						</div>
					</div>
				) : (
					/* Standard variant - original layout */
					<div
						className={cn(
							'flex size-full items-start justify-center overflow-hidden p-4 md:gap-6 md:p-6',
							layout === 'vertical' && 'flex-col gap-4',
							layout === 'horizontal' && 'flex-row gap-4 md:gap-6',
						)}
					>
						{/* Image Section */}
						{showImage && (
							<div
								className={cn(
									'flex shrink-0 items-center justify-center overflow-hidden',
									layout === 'vertical' &&
										'h-0 min-h-[200px] w-full grow basis-0 rounded-xl md:min-h-[240px]',
									layout === 'horizontal' &&
										'h-[72px] w-[66px] rounded-[12px] md:h-52 md:w-[200px] md:rounded-xl',
								)}
							>
								<img
									src={image}
									alt={imageAlt}
									className={cn(
										'size-full',
										layout === 'vertical' && 'object-cover object-center',
										layout === 'horizontal' && 'object-contain md:object-cover',
									)}
								/>
							</div>
						)}

						{/* Content Section */}
						<div
							className={cn(
								'flex flex-col',
								layout === 'horizontal' && 'flex-1 gap-4 md:gap-3',
								layout === 'vertical' && 'gap-3',
							)}
						>
							{/* Chip/Tag */}
							{showChip && (
								<div className="font-parkinsans inline-flex shrink-0 items-center justify-center self-start rounded-[4px] bg-green-500 px-2 py-0.5 text-sm leading-5 font-medium text-neutral-50">
									{chipText}
								</div>
							)}

							{/* Title and Description Container */}
							<div
								className={cn(
									'flex w-full flex-col',
									layout === 'horizontal' && 'gap-1 md:gap-3',
									layout === 'vertical' && 'gap-3',
								)}
							>
								{/* Title with optional icon */}
								<div className="flex w-full items-center gap-3">
									{showIcon && (
										<div className="flex size-6 shrink-0 items-center justify-center">
											{iconElement ||
												(icon ? (
													<Icon
														name={icon}
														size="md"
														className="text-blue-800"
													/>
												) : null)}
										</div>
									)}
									<h3
										className={cn(
											'font-parkinsans flex-1 font-medium text-blue-800',
											layout === 'vertical' && 'text-2xl leading-8',
											layout === 'horizontal' &&
												'text-base leading-5 md:text-2xl md:leading-8',
										)}
									>
										{title}
									</h3>
								</div>

								{/* Description */}
								{showDescription && (
									<p
										className={cn(
											'w-full text-sm font-normal text-neutral-700',
											layout === 'horizontal' &&
												'font-parkinsans md:font-inter leading-[22px] md:leading-[22px]',
											layout === 'vertical' && 'font-inter leading-[22px]',
										)}
									>
										{description}
									</p>
								)}
							</div>

							{/* Buttons Container */}
							{(showButton || showButton2) && (
								<div
									className={cn(
										'flex w-full flex-col gap-3',
										layout === 'horizontal' && 'md:flex-row',
									)}
								>
									{showButton && (
										<Button
											variant="primary"
											appearance="solid"
											size="m"
											onClick={onButtonClick}
											className={cn(
												'w-full',
												layout === 'horizontal' && showButton2 && 'md:flex-1',
											)}
										>
											{buttonText}
										</Button>
									)}
									{showButton2 && (
										<Button
											variant="primary"
											appearance="text"
											size="m"
											onClick={onButton2Click}
											className={cn(
												'w-full',
												layout === 'horizontal' && showButton && 'md:flex-1',
											)}
										>
											{button2Text}
										</Button>
									)}
								</div>
							)}
						</div>
					</div>
				)}
			</Comp>
		)
	},
)

CardImageTextExt.displayName = 'CardImageTextExt'

export { CardImageTextExt, cardImageTextExtVariants }
